import {
  ProjectLiteModel,
  ProjectModel,
  useGetProject,
  useGetProjects,
} from 'api-hooks/projects';
import { Option } from 'baseui/select';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import { useAuthorization } from 'hooks/use-authorization';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface Props {
  name: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: any) => void;
  onAfterDetailChange?: (value: ProjectModel) => void;
  noMargin?: boolean;
}

function transformer(item: ProjectLiteModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.code, item.name]),
  };
}

export default function ProjectListSelectInput(props: Props) {
  const { t } = useTranslation();
  const { label = t('common:project'), placeholder = t('common:project') } =
    props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const { can } = useAuthorization();

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetProjects,
    useDetailLazyQueryHook: useGetProject,
    // listKeyFunction: getProjectsKey,
    // detailKeyFunction: getProjectKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),

    renderCreate: !!can(AuthorizationRules.ProductsCreate),
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.ProjectCreate);
      props.onClose?.();
    }, [navigate, props]),

    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),

    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),

    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, ProjectModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),

    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      overrides={overrides}
      {...selectProps}
    />
  );
}
